import { zodResolver } from '@hookform/resolvers/zod';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IMaskInput } from 'react-imask';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import InputText from '~/components/InputText/InputText';
import { CHECKBOX_SIZE, Checkbox } from '~/components/isolated/Checkbox';
import Textarea from '~/components/Textarea/Textarea';

import styles from './GridFeedbackForm.module.scss';

const phoneRegex = new RegExp(
  /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/,
);

const GridFeedbackForm = () => {
  const { t } = useTranslation();
  const [phoneVal, setPhoneVal] = useState('');
  const [hasConsent, setHasConsent] = useState(false);
  const validationSchema = z.object({
    phone: z
      .string({
        required_error: t('errors:cantBeEmpty'),
        invalid_type_error: t('errors:cantBeEmpty'),
      })
      .min(1, t('errors:cantBeEmpty'))
      .regex(phoneRegex, t('phrases:invalidPhoneFormat')),
    message: z
      .string({
        required_error: t('errors:cantBeEmpty'),
        invalid_type_error: t('errors:cantBeEmpty'),
      })
      .min(1, t('errors:cantBeEmpty')),
  });
  type FeedbackSchema = z.infer<typeof validationSchema>;
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FeedbackSchema>({
    resolver: zodResolver(validationSchema),
  });
  const phoneProps = register('phone');
  const messageProps = register('message');

  // TODO: add request to backend
  const handleForm = async (data: FeedbackSchema) => {
    return await Promise.resolve(data);
  };

  const handleCodeChange = (val: string) => {
    setValue('phone', val);
    setPhoneVal(val);
  };

  return (
    <div className={styles.feedbackForm}>
      <div className={styles.feedbackFormHeader}>
        <p className={styles.feedbackFormTitle}>
          {t('dubaiExpressFeedbackTitle')}
        </p>
        <p className={styles.feedbackFormSubtitle}>
          {t('dubaiExpressFeedbackSubtitle')}
        </p>
      </div>
      <form
        className={styles.feedbackFormFields}
        onSubmit={handleSubmit(handleForm)}
      >
        <InputText
          classNameWrapper={styles.feedbackField}
          className={styles.feebackWrapper}
          classNameInput={styles.feedbackInput}
          placeholder={t('messagePhonePlaceholder')}
          error={errors.phone?.message ?? ''}
          refObj={phoneProps.ref}
          {...phoneProps}
        >
          <IMaskInput
            className={classes(['input-text', styles.feedbackInput])}
            mask="+000000000000000"
            type="tel"
            value={phoneVal}
            onAccept={handleCodeChange}
            autoComplete="phone-number"
            placeholder={t('messagePhoneNumber')}
          />
        </InputText>
        <Textarea
          classNameWrapper={classes([
            styles.feedbackField,
            styles.feedbackFieldMessage,
          ])}
          className={styles.feebackWrapper}
          classNameInput={styles.feedbackTextarea}
          placeholder={t('messageMessage')}
          error={errors.message?.message ?? ''}
          refObj={messageProps.ref}
          {...messageProps}
        />
        <button
          className={classes(['button _primary', styles.feedbackButton])}
          type="submit"
          disabled={!hasConsent}
        >
          {t('send')}
        </button>
        <Checkbox
          className={styles.feedbackConsent}
          checked={hasConsent}
          onChange={(event) => setHasConsent(event.target.checked)}
          size={CHECKBOX_SIZE.S}
          classes={{ label: styles.feedbackConsentLabel }}
          label={
            <>
              Согласен на{' '}
              <Link className={styles.feedbackConsentLink} to="/legals">
                обработку персональных данных
              </Link>
            </>
          }
        />
      </form>
    </div>
  );
};

export default observer(GridFeedbackForm);
